import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import {
  defaultAssociationTypeId,
  defaultHubspotMappingConfig,
  IHubspotMappingConfig,
  IHubspotMappingStatic,
  IHubspotMappingStatus,
  settingInputs,
} from './constants'
import { IMappingRow, recordFromRows, rowsFromRecord } from './mapping'
import { MappingTable } from './MappingTable'

const configKey = 'hubspotMapping'

const emptyStatic: IHubspotMappingStatic = {
  sourceKeys: {},
  grammar: [],
  starter: { lead: {}, contact: {} },
  dealStarter: { lead: {}, contact: {} },
  objectTypes: [],
  ownerSources: [],
}

/**
 * `showVendorPointer` is off when this renders on the vendor's own page, where a line pointing at
 * that page would be telling the reader to go where they already are.
 */
export const HubspotSyncConfig = ({ showVendorPointer = true }: { showVendorPointer?: boolean }) => {
  const [isLoading, setLoading] = useState(false)
  const [isChanged, setChanged] = useState(false)
  const [config, setConfig] = useState<IHubspotMappingConfig>(defaultHubspotMappingConfig())
  const [staticValues, setStaticValues] = useState<IHubspotMappingStatic>(emptyStatic)
  const [status, setStatus] = useState<IHubspotMappingStatus>({ active: false, readiness: [] })
  const [leadRows, setLeadRows] = useState<IMappingRow[]>([])
  const [contactRows, setContactRows] = useState<IMappingRow[]>([])
  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])

  useEffect(() => {
    load()
  }, [])

  const load = async () => {
    setLoading(true)
    try {
      const { static: values, status: statusValues, data } = await getAdminConfig(configKey)
      const nextStatic: IHubspotMappingStatic = { ...emptyStatic, ...(values || {}) }
      const nextConfig: IHubspotMappingConfig = { ...defaultHubspotMappingConfig(), ...(data || {}) }
      setStaticValues(nextStatic)
      setConfig(nextConfig)
      setStatus({ active: false, readiness: [], ...(statusValues || {}) })
      setLeadRows(rowsFromRecord(nextConfig.lead, nextStatic.sourceKeys))
      setContactRows(rowsFromRecord(nextConfig.contact, nextStatic.sourceKeys))
      setChanged(false)
    } finally {
      setLoading(false)
    }
  }

  const isDeals = config.objectType === 'deals'
  const noun = isDeals ? 'deal' : 'lead'
  const recordStarter = isDeals ? staticValues.dealStarter : staticValues.starter

  const inputs = useMemo(() => {
    const fields = settingInputs(loanGlobalStatus || {}, config.objectType)
    Object.keys(fields).forEach((key) => {
      if (fields[key].inputType === 'section') return
      fields[key].value = (config as any)[key]
    })
    // The owner only ever comes from the Account Executive when that is what the tenant picked.
    fields.fallbackOwnerEmail.visible = config.ownerSource === 'accountExecutive'
    return fields
  }, [config, loanGlobalStatus])

  const onChangeSetting = (key: string, value: any) => {
    const next = cloneDeep(config) as any
    next[key] = value
    // Changing the object carries the association id with it, as long as the id is still the one
    // that came with the object being left. The server normalises this too; doing it here is what
    // stops the field sitting on 578 after a switch to Deals and then changing under the admin on
    // the next load.
    if (
      key === 'objectType' &&
      Number(config.primaryContactAssociationTypeId) === defaultAssociationTypeId(config.objectType)
    )
      next.primaryContactAssociationTypeId = defaultAssociationTypeId(value)
    setConfig(next)
    setChanged(true)
  }

  const onChangeRows = (setRows: Function) => (rows: IMappingRow[]) => {
    setRows(rows)
    setChanged(true)
  }

  const onSubmit = async () => {
    const id = Number(config.primaryContactAssociationTypeId)
    const data: IHubspotMappingConfig = {
      ...config,
      lead: recordFromRows(leadRows),
      contact: recordFromRows(contactRows),
      primaryContactAssociationTypeId: !id || isNaN(id) ? defaultAssociationTypeId(config.objectType) : id,
    }
    setLoading(true)
    try {
      await setAdminConfig(configKey, data)
      toast('HubSpot Sync configuration has been saved.', { type: 'info' })
    } finally {
      setLoading(false)
    }
    // Reload so the readiness list and the saved mapping are the server's, not ours.
    await load()
  }

  /**
   * Where to go to fix the credential. On the vendor's own page that is the form above this
   * section, so pointing at the page the reader is already on would be no help.
   */
  const credentialLocation = showVendorPointer ? 'under Admin > Vendors > Hubspot' : 'in the form above'

  const banner = useMemo(() => {
    if (!config.enabled)
      return {
        className: 'bg-gray-100 text-gray-700',
        text: 'Off. Loans on this tenant are not sent to HubSpot.',
      }

    // The credential outranks the mapping's own readiness, because a complete mapping writes
    // nothing over a token HubSpot refuses, and that pair is what read as green while every call
    // came back 401. An older backend sends no `credential` at all, which must leave the previous
    // behaviour intact rather than showing a tenant a warning nothing can clear.
    const credential = status.credential
    // ⚠️ A leads 403 is not always about scopes at all. The Leads object needs Sales Hub
    // Professional or Enterprise and then has to be switched on in the account, and a portal
    // without it refuses the write with this very error, so the advice below cannot work. Seen on a
    // live tenant where the scope WAS granted and the portal had no Leads object: the banner sent
    // the admin to a ticked checkbox and said nothing about the only thing that would fix it.
    const leadsCaveat = credential?.missingScopes?.some((scope) => scope.startsWith('crm.objects.leads'))
      ? ' If that scope is already granted, check that the Leads object exists on this HubSpot account: it needs Sales Hub Professional or Enterprise and then Settings > Data Management > Objects. Switching the HubSpot object above to Deals avoids it.'
      : ''
    // ⚠️ A 403 is NOT a bad token. HubSpot accepted it and refused the call for want of a scope, so
    // replacing the token fixes nothing and this banner used to advise exactly that. The scopes are
    // the whole remediation, so they are named here rather than behind a link to HubSpot's docs.
    if (credential?.state === 'rejected' && credential.status === 403)
      return {
        className: 'bg-red-50 text-red-800',
        text: credential.missingScopes?.length
          ? `On, and the access token is valid, but the HubSpot app has not been granted ${
              credential.missingScopes.length === 1 ? 'a scope' : 'scopes'
            } this sync needs: ${credential.missingScopes.join(', ')}. Add ${
              credential.missingScopes.length === 1 ? 'it' : 'them'
            } to the private app in HubSpot under Settings > Integrations > Private apps, then save again. Nothing reaches HubSpot until then.${leadsCaveat}`
          : `On, and the access token is valid, but HubSpot refused the call for want of a scope${
              credential.detail ? ` (${credential.detail})` : ''
            }. Check the private app's scopes in HubSpot under Settings > Integrations > Private apps, then save again. Nothing reaches HubSpot until then.`,
      }
    if (credential?.state === 'rejected')
      return {
        className: 'bg-red-50 text-red-800',
        text: `On, but HubSpot is refusing the access token${
          credential.detail ? ` (${credential.detail})` : ''
        }. Nothing is reaching HubSpot until it is replaced ${credentialLocation}.`,
      }
    if (credential?.state === 'missing')
      return {
        className: 'bg-amber-50 text-amber-800',
        text: `On, but no HubSpot access token has been saved for this tenant. Add one ${credentialLocation}.`,
      }
    if (credential?.state === 'vendorOff')
      return {
        className: 'bg-amber-50 text-amber-800',
        text: `On here, but the Hubspot vendor switch is off, so nothing is sent. Turn it on ${credentialLocation}.`,
      }

    if (status.active && !status.readiness.length)
      return { className: 'bg-green-50 text-green-800', text: 'On. Loans are sent to HubSpot as they change.' }
    return {
      className: 'bg-amber-50 text-amber-800',
      text: 'On, but not yet complete. Nothing is sent until the following is in place:',
    }
  }, [config.enabled, status, credentialLocation])

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} label="Save Changes" onSave={onSubmit} />

      <div className={`rounded p-3 mb-4 text-sm ${banner.className}`}>
        <p>{banner.text}</p>
        {!!status.readiness.length && config.enabled && (
          <ul className="list-disc ml-5 mt-1">
            {status.readiness.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-sm text-gray-600 mb-6">
        A loan is pushed when it is created and whenever its status changes, and a loan with no borrower email is
        skipped.
        {showVendorPointer && ' The private app token and the vendor switch live under Admin > Vendors > Hubspot.'}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {Object.keys(inputs).map((key) => {
          const input = inputs[key]
          if (input.visible === false) return null
          return (
            <div className={`input md:col-span-${input.span || 1}`} key={key}>
              <RenderInput input={input} Key={key} onChange={(_: string, value: any) => onChangeSetting(key, value)} />
            </div>
          )
        })}
      </div>

      <MappingTable
        title={isDeals ? 'Deal Properties' : 'Lead Properties'}
        description={`Each row fills one property on the HubSpot ${noun}. A source that resolves to nothing is left out
          of the request, so a blank in Nexys never overwrites what a rep typed in HubSpot.`}
        rows={leadRows}
        sourceKeys={staticValues.sourceKeys}
        starter={recordStarter.lead}
        onChange={onChangeRows(setLeadRows)}
      />

      <MappingTable
        title="Contact Properties"
        description={`The primary borrower, as a HubSpot contact associated with the ${noun}. The contact is matched on
          email, so an email property is required.`}
        rows={contactRows}
        sourceKeys={staticValues.sourceKeys}
        starter={recordStarter.contact}
        onChange={onChangeRows(setContactRows)}
      />

      {!!staticValues.grammar.length && (
        <details className="mb-6 text-sm">
          <summary className="cursor-pointer text-shade-blue">Writing a custom expression</summary>
          <ul className="mt-2 ml-5 list-disc text-gray-700">
            {staticValues.grammar.map((line) => (
              <li className="font-mono text-xs py-0.5" key={line}>
                {line}
              </li>
            ))}
          </ul>
        </details>
      )}

      <Button disabled={!isChanged} onClick={onSubmit}>
        Save
      </Button>
    </div>
  )
}
